import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Gamepad2, Zap, Clock, CheckCircle, XCircle,
  Trophy, Crown, Medal, Loader2, Hash, Star, LogIn, AlertTriangle, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Safe localStorage helper for Safari private browsing
const safeStorage = {
  get: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  set: (key, val) => { try { localStorage.setItem(key, val); } catch {} },
  remove: (key) => { try { localStorage.removeItem(key); } catch {} },
};

const KahootPlayerPage = () => {
  const [step, setStep] = useState('loading');
  const [gameCode, setGameCode] = useState('');
  const [userName, setUserName] = useState('');
  const [userId, setUserId] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [playerCount, setPlayerCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIdx, setQuestionIdx] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timer, setTimer] = useState(0);
  const [answerResult, setAnswerResult] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [error, setError] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (code) setGameCode(code);
    } catch {}

    const token = safeStorage.get('token');
    if (token) {
      axios.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          const u = r.data;
          setUserName(`${u.prenom || ''} ${u.nom || ''}`.trim());
          setUserId(u.user_id);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setStep('join');
        })
        .catch(() => {
          safeStorage.remove('token');
          setStep('login');
        });
    } else {
      setStep('login');
    }

    return () => {
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleLogin = async () => {
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setError('Entrez votre email et mot de passe');
      return;
    }
    setError('');
    setLoginLoading(true);
    try {
      const r = await axios.post('/auth/login', { email: loginEmail.trim(), password: loginPassword.trim() });
      const token = r.data.access_token;
      const user = r.data.user;
      safeStorage.set('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUserName(`${user.prenom || ''} ${user.nom || ''}`.trim());
      setUserId(user.user_id);
      setStep('join');
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Email ou mot de passe incorrect');
    } finally { setLoginLoading(false); }
  };

  const joinGame = () => {
    const code = gameCode.trim();
    if (!code) { setError('Entrez le code de la partie'); return; }
    if (!userName) { setError('Erreur: nom non disponible'); return; }
    setError('');

    try {
      const wsProtocol = BACKEND_URL.indexOf('https') === 0 ? 'wss' : 'ws';
      const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
      const wsUrl = wsProtocol + '://' + wsHost + '/api/foad/kahoot/ws/player/' + code;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = function() {
        var token = safeStorage.get('token') || '';
        ws.send(JSON.stringify({ action: 'join', nickname: userName, user_id: userId, token: token }));
      };

      ws.onmessage = function(event) {
        try {
          var msg = JSON.parse(event.data);
          if (msg.type === 'ping') return;
          handleMessage(msg);
        } catch (e) {
        }
      };

      ws.onerror = function() {
        setError('Erreur de connexion au serveur. Verifiez le code et reessayez.');
      };

      ws.onclose = function(event) {
        if (event.code !== 1000 && event.code !== 1001) {
          setError('Connexion perdue. Rechargez la page pour reessayer.');
        }
      };
    } catch (e) {
      setError('Impossible de se connecter. Verifiez votre connexion internet.');
    }
  };

  const handleMessage = (msg) => {
    if (msg.type === 'error') { setError(msg.message); return; }
    if (msg.type === 'joined') {
      setPlayerId(msg.player_id);
      setPlayerCount(msg.player_count);
      setStep(msg.game_status === 'playing' ? 'question' : 'waiting');
    }
    if (msg.type === 'player_count') { setPlayerCount(msg.count); }
    if (msg.type === 'question') {
      setCurrentQuestion(msg);
      setQuestionIdx(msg.question_idx);
      setTotalQuestions(msg.total_questions);
      setAnswerResult(null);
      setStep('question');
      setQuestionStartTime(Date.now());
      startTimer(msg.time_limit);
    }
    if (msg.type === 'answer_confirmed') {
      setAnswerResult(msg);
      setTotalScore(msg.total_score);
      setStep('answered');
      if (timerRef.current) clearInterval(timerRef.current);
    }
    if (msg.type === 'game_over') {
      setLeaderboard(msg.leaderboard || []);
      setStep('finished');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const startTimer = (seconds) => {
    setTimer(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(function() {
      setTimer(function(prev) {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const submitAnswer = (selected) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    var timeMs = Date.now() - (questionStartTime || Date.now());
    wsRef.current.send(JSON.stringify({ action: 'answer', selected: [selected], time_ms: timeMs }));
  };

  // ============= LOADING =============
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center" data-testid="kahoot-loading">
        <Loader2 className="h-10 w-10 animate-spin text-white" />
      </div>
    );
  }

  // ============= LOGIN =============
  if (step === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4" data-testid="kahoot-login">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Gamepad2 className="h-12 w-12 text-white mx-auto mb-2" />
            <h1 className="text-2xl font-black text-white">Kahoot!</h1>
            <p className="text-violet-200 text-sm mt-1">Connectez-vous pour rejoindre</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-2xl space-y-3">
            {error && <p className="text-red-500 text-sm text-center font-medium bg-red-50 rounded-lg p-2" data-testid="login-error">{error}</p>}
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Email</label>
              <input value={loginEmail} onChange={function(e) { setLoginEmail(e.target.value); }} placeholder="votre@email.fr" type="email"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none"
                autoComplete="email" autoCapitalize="none" autoCorrect="off"
                data-testid="login-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Mot de passe</label>
              <input value={loginPassword} onChange={function(e) { setLoginPassword(e.target.value); }} placeholder="Mot de passe" type="password"
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none"
                autoComplete="current-password"
                data-testid="login-password"
                onKeyDown={function(e) { if (e.key === 'Enter') handleLogin(); }} />
            </div>
            <Button onClick={handleLogin} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-4 text-base font-bold rounded-xl" disabled={loginLoading} data-testid="login-btn">
              {loginLoading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <LogIn className="h-5 w-5 mr-2" />}
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============= JOIN =============
  if (step === 'join') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4" data-testid="kahoot-join">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Gamepad2 className="h-12 w-12 text-white mx-auto mb-2" />
            <h1 className="text-2xl font-black text-white">Kahoot!</h1>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-2xl space-y-4">
            <div className="bg-violet-50 rounded-xl p-3 text-center">
              <p className="text-xs text-violet-500">Connecte en tant que</p>
              <p className="text-base font-bold text-violet-700" data-testid="player-name">{userName}</p>
            </div>
            {error && <p className="text-red-500 text-sm text-center font-medium bg-red-50 rounded-lg p-2">{error}</p>}
            <input value={gameCode} onChange={function(e) { setGameCode(e.target.value); }} placeholder="Code de la partie"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-4 text-center text-2xl font-bold tracking-widest focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none"
              inputMode="numeric" pattern="[0-9]*" autoComplete="off"
              data-testid="join-code-input" maxLength={6}
              onKeyDown={function(e) { if (e.key === 'Enter') joinGame(); }} />
            <Button onClick={joinGame} className="w-full bg-violet-600 hover:bg-violet-700 text-white py-4 text-base font-bold rounded-xl" data-testid="join-btn">
              <Zap className="h-5 w-5 mr-2" /> Rejoindre
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============= WAITING =============
  if (step === 'waiting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4" data-testid="kahoot-waiting">
        <div className="text-center text-white">
          <div className="animate-bounce mb-6"><Gamepad2 className="h-16 w-16 mx-auto" /></div>
          <h2 className="text-2xl font-black mb-2">Vous etes connecte !</h2>
          <p className="text-violet-200 mb-2">{userName}</p>
          <p className="text-violet-300 text-sm">En attente du formateur...</p>
          <p className="text-violet-200 mt-4 text-lg font-bold">{playerCount} joueur(s)</p>
          <Loader2 className="h-8 w-8 animate-spin mx-auto mt-6 text-violet-300" />
        </div>
      </div>
    );
  }

  // ============= QUESTION =============
  if (step === 'question' && currentQuestion) {
    var opts = currentQuestion.options || [];
    var colors = ['bg-red-500 active:bg-red-600', 'bg-blue-500 active:bg-blue-600', 'bg-amber-500 active:bg-amber-600', 'bg-green-500 active:bg-green-600'];
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="kahoot-question-player">
        <div className="p-3 flex items-center justify-between">
          <span className="text-white/60 text-sm font-medium">Q{questionIdx + 1}/{totalQuestions}</span>
          <div className={'flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg ' + (timer <= 3 ? 'bg-red-500 text-white animate-pulse' : 'bg-white/10 text-white')}>
            <Clock className="h-4 w-4" /> {timer}
          </div>
          <span className="text-violet-400 text-sm font-bold">{totalScore} pts</span>
        </div>
        <div className="px-4 py-4 text-center">
          <p className="text-white text-lg font-bold leading-relaxed">{currentQuestion.texte}</p>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-3 p-3">
          {opts.map(function(o, i) {
            return (
              <button key={i} onClick={function() { submitAnswer(o.idx !== undefined ? o.idx : i); }}
                className={colors[i % 4] + ' rounded-xl text-white font-bold text-lg flex items-center justify-center p-4 shadow-lg transform active:scale-95 transition-transform'}
                data-testid={'answer-btn-' + i}>
                {o.texte}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ============= ANSWERED =============
  if (step === 'answered') {
    var isCorrect = answerResult && answerResult.correct;
    return (
      <div className={'min-h-screen flex items-center justify-center p-4 ' + (isCorrect ? 'bg-green-500' : 'bg-red-500')} data-testid="kahoot-answered">
        <div className="text-center text-white">
          {isCorrect ? (
            <>
              <CheckCircle className="h-20 w-20 mx-auto mb-4" />
              <h2 className="text-3xl font-black mb-2">Correct !</h2>
              <p className="text-xl font-bold mb-1">+{answerResult.points_earned} pts</p>
              <p className="text-green-200 text-sm">Temps: {(answerResult.time_ms / 1000).toFixed(1)}s</p>
            </>
          ) : (
            <>
              <XCircle className="h-20 w-20 mx-auto mb-4" />
              <h2 className="text-3xl font-black mb-2">Faux !</h2>
              <p className="text-red-200 text-sm">+0 pts</p>
            </>
          )}
          <p className="mt-6 text-white/80 text-lg font-bold">Score: {totalScore} pts</p>
          <Loader2 className="h-6 w-6 animate-spin mx-auto mt-6 text-white/50" />
          <p className="text-white/50 text-xs mt-2">En attente de la question suivante...</p>
        </div>
      </div>
    );
  }

  // ============= FINISHED =============
  if (step === 'finished') {
    var myRank = leaderboard.findIndex(function(p) { return p.player_id === playerId; });
    var myData = myRank >= 0 ? leaderboard[myRank] : null;
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 p-4" data-testid="kahoot-finished">
        <div className="max-w-md mx-auto">
          <div className="text-center py-6">
            <Trophy className="h-14 w-14 text-amber-400 mx-auto mb-3" />
            <h1 className="text-3xl font-black text-white">Partie terminee !</h1>
          </div>
          {myData && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center text-white mb-6">
              <p className="text-violet-200 text-sm mb-1">Votre resultat</p>
              <p className="text-4xl font-black">{myRank + 1}{myRank === 0 ? 'er' : 'eme'}</p>
              <p className="text-xl font-bold text-violet-200">{myData.score} points</p>
              <p className="text-sm text-violet-300">{myData.correct_count}/{totalQuestions} bonnes reponses</p>
            </div>
          )}
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="p-3 bg-slate-50 border-b">
              <h3 className="font-bold text-slate-700 text-sm flex items-center gap-2"><Hash className="h-4 w-4" /> Classement</h3>
            </div>
            <div className="divide-y max-h-80 overflow-y-auto">
              {leaderboard.map(function(p, i) {
                return (
                  <div key={p.player_id} className={'flex items-center justify-between px-3 py-2.5 ' + (p.player_id === playerId ? 'bg-violet-50' : '')}>
                    <div className="flex items-center gap-2.5">
                      <span className={'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ' + (i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-100 text-slate-600')}>
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{p.nickname} {p.player_id === playerId && <Star className="h-3 w-3 inline text-amber-500" />}</p>
                        <p className="text-xs text-slate-400">{p.correct_count} bonnes</p>
                      </div>
                    </div>
                    <span className="font-bold text-violet-600 text-sm">{p.score}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============= FALLBACK (should never happen) =============
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center p-4" data-testid="kahoot-error">
      <div className="text-center text-white">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-400" />
        <h2 className="text-xl font-bold mb-2">Une erreur est survenue</h2>
        <p className="text-violet-200 text-sm mb-6">Rechargez la page pour reessayer</p>
        <Button onClick={function() { window.location.reload(); }} className="bg-white text-violet-700 hover:bg-violet-50 font-bold px-6 py-3 rounded-xl" data-testid="reload-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Recharger
        </Button>
      </div>
    </div>
  );
};

export default KahootPlayerPage;
